import { useLazyQuery } from "@apollo/client";
import equal from "deep-equal";
import { ComponentProps, useCallback, useEffect, useMemo, useState } from "react";

import ArticleFilters from "@components/ArticleFilters";
import ArticleTableRow from "@components/ArticleTableRow";
import HandleQuery from "@components/HandleQuery";
import Table, { TableHeader } from "@components/Table";
import useSubscribeToAllArticles from "@hooks/subscriptions/useSubscribeToAllArticles";
import useNodesFromConnection from "@hooks/useNodesFromConnection";
import {
  ArticleContentTypeEnum,
  ArticleSortByEnum,
  GetArticlesDocument,
  GetArticlesQuery,
  GetArticlesQueryVariables,
  GetArticlesSummaryDocument,
  GetArticlesSummaryQuery,
  GetArticlesSummaryQueryVariables,
  SortDirectionEnum
} from "@typing/Generated";
import { contentConditionKindsForSources } from "@utils/modelUtils";

type Filters = ComponentProps<typeof ArticleFilters>["filters"];

type Props = {
  contentTypes: ArticleContentTypeEnum[];
  includeHidden: boolean;
  journeyId: string | null;
  onSelect?: (article: { id: string; title: string }) => void;
  selectedArticleIds?: string[];
  skipContentConditionFilter?: boolean;
  skipScroll?: boolean;
};

const ArticlesTable = ({
  contentTypes,
  includeHidden,
  journeyId,
  onSelect,
  selectedArticleIds,
  skipContentConditionFilter = false,
  skipScroll = false
}: Props) => {
  const [getArticles, getArticlesQuery] = useLazyQuery<GetArticlesQuery, GetArticlesQueryVariables>(
    GetArticlesDocument
  );

  const [getArticlesSummary, getArticlesSummaryQuery] = useLazyQuery<
    GetArticlesSummaryQuery,
    GetArticlesSummaryQueryVariables
  >(GetArticlesSummaryDocument);

  useSubscribeToAllArticles(() => {
    getArticlesQuery.refetch();
    getArticlesSummaryQuery.refetch();
  });

  const [filters, setFilters] = useState<Filters>({
    contentConditionSource: undefined,
    contentType: null,
    focusArea: undefined,
    search: ""
  });
  const [sortBy, setSortBy] = useState<ArticleSortByEnum>(ArticleSortByEnum.CREATED_AT);
  const [sortDirection, setSortDirection] = useState<SortDirectionEnum>(SortDirectionEnum.DESCENDING);

  const handleFilterChange = useCallback(
    (newFilters: Filters) => {
      if (!equal(newFilters, filters)) {
        setFilters(newFilters);
      }
    },
    [filters]
  );

  const isWebinars = useMemo(
    () => contentTypes.length === 1 && contentTypes[0] === ArticleContentTypeEnum.WEBINAR,
    [contentTypes]
  );

  const isMyStories = useMemo(
    () => contentTypes.length === 1 && contentTypes[0] === ArticleContentTypeEnum.MY_STORY,
    [contentTypes]
  );

  useEffect(() => {
    const contentConditionKinds = contentConditionKindsForSources([filters.contentConditionSource]);
    getArticles({
      variables: {
        ...filters,
        contentConditionKinds,
        contentTypes: filters.contentType ? [filters.contentType] : contentTypes,
        includeHidden,
        sortBy,
        sortDirection
      }
    });
  }, [contentTypes, filters, getArticles, includeHidden, sortBy, sortDirection]);

  useEffect(() => {
    if (contentTypes.includes(ArticleContentTypeEnum.TEXT)) {
      getArticlesSummary();
    }
  }, [contentTypes, getArticlesSummary]);

  const articles = useNodesFromConnection(getArticlesQuery.data?.articles);

  const tableHeaders: TableHeader<ArticleSortByEnum>[] = [
    { columnSortBy: ArticleSortByEnum.CONTENT_TYPE },
    { columnSortBy: ArticleSortByEnum.TITLE },
    { messageKey: "pages.admin.articles.tableHeadings.tags" },
    { columnSortBy: ArticleSortByEnum.WEBINAR_START_TIME, skip: !isWebinars },
    { columnSortBy: ArticleSortByEnum.CREATED_AT },
    { columnSortBy: ArticleSortByEnum.UPDATED_AT },
    { columnSortBy: ArticleSortByEnum.AUTHOR },
    { messageKey: "pages.admin.articles.tableHeadings.focus_area", skip: isWebinars || isMyStories },
    { messageKey: "pages.admin.articles.tableHeadings.country_codes" },
    { columnSortBy: ArticleSortByEnum.WEBINAR_STATUS, skip: !isWebinars },
    { messageKey: "pages.admin.articles.tableHeadings.hidden" },
    { messageKey: "pages.admin.articles.tableHeadings.actions" }
  ];

  return (
    <>
      <ArticleFilters
        articleSummary={getArticlesSummaryQuery.data?.articlesSummary}
        filters={filters}
        onChange={handleFilterChange}
        skipContentConditions={skipContentConditionFilter}
        skipQueryParams={!!onSelect}
      />
      <HandleQuery query={getArticlesQuery}>
        {data => (
          <Table<ArticleSortByEnum>
            fetchMore={getArticlesQuery.fetchMore}
            headerKey="pages.admin.articles.tableHeadings"
            headers={tableHeaders}
            pageInfo={data?.articles.pageInfo}
            setSortBy={setSortBy}
            setSortDirection={setSortDirection}
            skipScroll={skipScroll}
            sortBy={sortBy}
            sortDirection={sortDirection}
          >
            {articles.map(article => (
              <ArticleTableRow
                article={article}
                journeyId={journeyId}
                key={article.id}
                onSelect={onSelect}
                selectedArticleIds={selectedArticleIds}
                showMyStoryFields={isMyStories}
                showWebinarFields={isWebinars}
              />
            ))}
          </Table>
        )}
      </HandleQuery>
    </>
  );
};

export default ArticlesTable;
